import React, { useMemo, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import './StatusIndicator.css';
import { selectPendingPromptData } from '../store/selectors';

// Fun loading tips
const LOADING_TIPS = [
  "Great design takes time...",
  "Mixing your creative ingredients...",
  "AI is painting your vision...",
  "Almost there, perfection awaits...",
  "Crafting something beautiful...",
];

const StatusIndicator = ({ status }) => {
  const userInputs = useSelector(selectPendingPromptData);
  const [tipIndex, setTipIndex] = useState(0);

  // Rotate tips every 4 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setTipIndex(prev => (prev + 1) % LOADING_TIPS.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  // Extract floating elements from user inputs
  const floatingElements = useMemo(() => {
    if (!userInputs) return { colors: [], texts: [], images: [] };

    const colors = [];
    const texts = [];
    const images = [];

    // Look for color fields in dynamic inputs
    Object.entries(userInputs).forEach(([key, value]) => {
      if (key.toLowerCase().includes('color') || key.toLowerCase().includes('palette')) {
        if (Array.isArray(value)) {
          value.forEach(c => {
            const colorStr = String(c).trim();
            if (colorStr && !colors.includes(colorStr)) colors.push(colorStr);
          });
        } else if (typeof value === 'string') {
          value.split(/[,\s]+/).filter(v => v.trim()).forEach(c => {
            if (!colors.includes(c.trim())) colors.push(c.trim());
          });
        }
      }
    });

    // Add idea/prompt (italic style)
    if (userInputs.idea) {
      texts.push({ type: 'idea', value: userInputs.idea, style: 'italic' });
    }

    // Add font
    if (userInputs.typography) {
      const fontValue = Array.isArray(userInputs.typography) 
        ? userInputs.typography.join(', ') 
        : userInputs.typography;
      texts.push({ type: 'typography', value: fontValue, style: 'font' });
    }

     // Handle dynamicInput separately (it's an object with camelCase keys)
     if (userInputs.dynamicInput && typeof userInputs.dynamicInput === 'object') {
      Object.entries(userInputs.dynamicInput).forEach(([key, value]) => {
        if (value === null || value === undefined || value === '') return;
        const displayValue = Array.isArray(value) 
          ? value.join(', ')
          : String(value);
        texts.push({ type: key, value: displayValue, style: 'normal' });
      });
    }

    const validFieldIds = new Set();
    if (userInputs.fields && Array.isArray(userInputs.fields)) {
      userInputs.fields.forEach(field => {
        if (field.id) {
          validFieldIds.add(field.id);
        }
      });
    }

    Object.entries(userInputs).forEach(([key, value]) => {
      if (['idea', 'projectName', 'typography', 'color', 'assets', 'fields', 'dynamicInput', 'availableTags', 'selectedTag'].includes(key)) return;
      if (!value) return;
      
      if (validFieldIds.size > 0 && !validFieldIds.has(key)) return;

      const displayValue = Array.isArray(value) 
        ? value.join(', ')
        : (typeof value === 'string' ? value : String(value));
      texts.push({ type: key, value: displayValue, style: 'normal' });
    });

    // Add uploaded assets as images
    if (userInputs.assets && Array.isArray(userInputs.assets)) {
      userInputs.assets.forEach(asset => {
        if (asset instanceof File) {
          images.push(URL.createObjectURL(asset));
        } else if (typeof asset === 'string') {
          images.push(asset);
        }
      });
    }

    return { colors, texts, images };
  }, [userInputs]);

  const progress = status?.progress ?? 0;

  // Get status message
  const getStatusMessage = () => {
    const statusMsg = status?.statusMessage || status?.status;
    switch (statusMsg) {
      case 'searching': return 'Searching for inspiration';
      case 'constructing_prompts': return 'Crafting your vision';
      case 'generating': return 'Creating designs';
      case 'completed': return 'Complete!';
      default: return 'Preparing';
    }
  };

  // Predefined positions for floating elements with orbit parameters
  // Each element has different orbit radius and duration for varied speeds
  const positions = [
    { left: '8%', top: '15%', rotate: -12, orbitX: 50, orbitY: 40, orbitDuration: 20, floatDuration: 5 },
    { left: '75%', top: '8%', rotate: 15, orbitX: -45, orbitY: 35, orbitDuration: 28, floatDuration: 7 },
    { left: '15%', top: '55%', rotate: -8, orbitX: 60, orbitY: -50, orbitDuration: 35, floatDuration: 6 },
    { left: '70%', top: '45%', rotate: 10, orbitX: -55, orbitY: 45, orbitDuration: 22, floatDuration: 8 },
    { left: '45%', top: '70%', rotate: -5, orbitX: 40, orbitY: 55, orbitDuration: 30, floatDuration: 5.5 },
    { left: '25%', top: '30%', rotate: 8, orbitX: -35, orbitY: -40, orbitDuration: 25, floatDuration: 6.5 },
    { left: '60%', top: '25%', rotate: -10, orbitX: 50, orbitY: -35, orbitDuration: 32, floatDuration: 7.5 },
    { left: '85%', top: '60%', rotate: 12, orbitX: -60, orbitY: 50, orbitDuration: 18, floatDuration: 5 },
  ];

  return (
    <div className="floating-loading-screen">
      {/* Background glow effects */}
      <div className="bg-glow bg-glow-1" />
      <div className="bg-glow bg-glow-2" />

      {/* Decorative geometric elements */}
      <div className="deco-ring deco-ring-1" />
      <div className="deco-ring deco-ring-2" />
      <div className="deco-line deco-line-1" />
      <div className="deco-line deco-line-2" />
      <div className="deco-dot deco-dot-1" />
      <div className="deco-dot deco-dot-2" />
      <div className="deco-dot deco-dot-3" />

      {/* Floating Images with orbit effect */}
      {floatingElements.images.map((img, index) => {
        const pos = positions[index % positions.length];
        return (
          <div
            key={`img-${index}`}
            className="floating-image"
            style={{
              left: pos.left,
              top: pos.top,
              '--rotate': `${pos.rotate}deg`,
              '--orbit-x': `${pos.orbitX}px`,
              '--orbit-y': `${pos.orbitY}px`,
              '--orbit-duration': `${pos.orbitDuration}s`,
              '--float-duration': `${pos.floatDuration}s`,
              animationDelay: `${index * 0.3}s, ${index * 2}s`
            }}
          >
            <img src={img} alt={`Asset ${index + 1}`} />
          </div>
        );
      })}

      {/* Floating Colors */}
      {floatingElements.colors.length > 0 && (
        <div className="floating-colors-group">
          {floatingElements.colors.slice(0, 4).map((color, index) => (
            <div
              key={`color-${index}`}
              className="floating-color-dot"
              style={{
                backgroundColor: getColorValue(color),
                animationDelay: `${index * 0.2}s`
              }}
            />
          ))}
        </div>
      )}

      {/* Floating Texts with orbit effect */}
      {floatingElements.texts.map((text, index) => {
        const pos = positions[(index + 3) % positions.length];
        return (
          <div
            key={`text-${text.type}-${index}`}
            className={`floating-text floating-text-${text.style}`}
            style={{
              left: pos.left,
              top: pos.top,
              '--rotate': `${pos.rotate}deg`,
              '--orbit-x': `${pos.orbitX * 0.8}px`,
              '--orbit-y': `${pos.orbitY * 0.8}px`,
              '--orbit-duration': `${pos.orbitDuration * 1.2}s`,
              '--float-duration': `${pos.floatDuration}s`,
              animationDelay: `${index * 0.4}s, ${index * 3}s`
            }}
          >
            {text.value}
          </div>
        );
      })}

      {/* Center Content */}
      <div className="loading-center-content">
        {/* KURA Logo */}
        <img src="/KURA-logo.png" alt="KURA" className="loading-logo" />

        {/* Status Message */}
        <div className="status-message">{getStatusMessage()}</div>

        {/* Progress Ring */}
        <div className="loading-progress-ring">
          <svg viewBox="0 0 100 100">
            <circle
              className="progress-ring-bg"
              cx="50"
              cy="50"
              r="45"
            />
            <circle
              className="progress-ring-fill"
              cx="50"
              cy="50"
              r="45"
              style={{
                strokeDasharray: `${progress * 2.83} 283`
              }}
            />
          </svg>
          <span className="progress-percent">{Math.round(progress)}%</span>
        </div>

        {/* Loading Tip */}
        <div className="loading-tip" key={tipIndex}>
          {LOADING_TIPS[tipIndex]}
        </div>
      </div>
    </div>
  );
};

// Helper to convert color names to actual colors
const getColorValue = (color) => {
  const colorMap = {
    'red': '#E74C3C',
    'orange': '#F39C12',
    'yellow': '#F1C40F',
    'green': '#82CD59',
    'blue': '#3498DB',
    'purple': '#9B59B6',
    'pink': '#E91E8C',
    'warm red': '#E74C3C',
    'golden': '#D4AF37',
    'sage': '#9DC183',
    'navy': '#1A237E',
    'coral': '#FF7F50',
    'teal': '#008080',
  };

  const lowerColor = color.toLowerCase().trim();
  return colorMap[lowerColor] || color;
};

export default StatusIndicator;
